// HeaderNavDropdown.js - A NavTab-styled trigger (icon + label + chevron)
// that opens a HeaderDropdown card of HeaderMenuItem actions. Used by
// TeacherNavButtons for "Create ▾" (Tests / Collections / Lesson plans) and
// "Manage ▾" (Students) — see docs/superpowers/specs/2026-09-15-header-redesign.md,
// Round 2 / WP7 — and by StudentNavButtons for "Programs ▾" (Lessons /
// Discovery Journeys / STEM Missions / Summer Camp), which additionally
// uses the `{ type: "section", key, label }` item form for group headers
// and the `note` item field for a muted trailing label (e.g. "in progress
// 42%") — see docs/superpowers/specs/2026-09-16-home-redesign.md §11 WP10.

import React, { useState } from "react";
import { Text, TouchableOpacity } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { CopilotStep, walkthroughable } from "react-native-copilot";

import HeaderDropdown, { HeaderMenuItem, HeaderMenuSection } from "./HeaderDropdown";
import NavTab from "./NavTab";
import { HEADER, menuColors } from "./headerTheme";

const WalkthroughableTouchableOpacity = walkthroughable(TouchableOpacity);

export default function HeaderNavDropdown({
  label,
  icon,
  isActive = false,
  showLabels = true,
  items = [],
  isDarkMode,
  styles: h,
  t = (s) => s,
  testID,
  copilotName,
  copilotText,
  copilotOrder,
}) {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  const colors = menuColors(isDarkMode);

  const trigger = (
    <NavTab
      as={copilotName ? WalkthroughableTouchableOpacity : TouchableOpacity}
      onPress={() => setOpen((prev) => !prev)}
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{ expanded: open }}
      title={label}
      testID={testID}
      isActive={isActive}
      showLabels={showLabels}
      iconName={icon}
      label={label}
      trailingIcon={open ? "chevron-up" : "chevron-down"}
      styles={h}
    />
  );

  const triggerNode = copilotName ? (
    <CopilotStep text={copilotText} order={copilotOrder} name={copilotName}>
      {trigger}
    </CopilotStep>
  ) : (
    trigger
  );

  return (
    <HeaderDropdown
      open={open}
      onClose={close}
      isDarkMode={isDarkMode}
      align="left"
      width={220}
      trigger={triggerNode}
      testID={testID}
    >
      {items.map((item) =>
        item.type === "section" ? (
          <HeaderMenuSection key={item.key} isDarkMode={isDarkMode} title={item.label} />
        ) : (
          <HeaderMenuItem
            key={item.key}
            isDarkMode={isDarkMode}
            icon={item.icon}
            label={item.label}
            disabled={item.disabled}
            onPress={() => {
              item.onPress();
              close();
            }}
            right={
              item.active ? (
                <MaterialCommunityIcons name="check" size={16} color={HEADER.brand} />
              ) : item.note ? (
                <Text style={{ fontSize: 12, color: colors.muted }}>{item.note}</Text>
              ) : null
            }
            testID={testID ? `${testID}-${item.key}` : undefined}
          />
        )
      )}
    </HeaderDropdown>
  );
}
